import { useEffect, useRef, useState } from 'react';
import { TravellerForm } from './form';
import { TravellerSubmit } from './traveller-submit';
import { useFakeTraveller } from 'lib/hooks/useFakeTraveller';
import { ContinueCta } from 'components/MobileCta/Continue';
import { useDevice } from 'lib/hooks/useDevice';
import { useRudderStack } from 'lib/hooks/useRudderStack';
import { useFillTraveller } from 'lib/hooks/useFillTraveller';
import { TTravellerSubmit } from 'lib/types/traveller';
import { useSubmitTraveller } from 'lib/hooks/useSubmitTraveller';
import { ChangeDate as ChangeDateModal } from '../../../changeDate/ChangeDate';
import { SelectionAvailability } from 'lib/types/availability';
import { FlexibleDepartureDecision } from 'lib/types/product';
import { SelectedRoom } from 'lib/types/trip';
import { ValidationHelper } from 'lib/helper/validation/validationHelper';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { setShouldSelectDate } from '@/state/slices/selectDate';
import { useCreatorHostedSession } from 'lib/hooks/useCreatorHostedSession';
import { useTenant } from 'lib/hooks/useTenant';

type AdditionalProps = {
  selectedRooms: SelectedRoom[];
};

const TravellerStage = ({
  submitCreateTraveller: submit,
  travellerState: state,
  createTravellerDispatch: dispatchCreateTraveller,
  validateTraveller,
  travellerValidationState,
  travellerValidationDispatch,
  selectedRooms,
  isCreateTravellerLoading,
  createTravellerIsGotToGo,
}: TTravellerSubmit & AdditionalProps) => {
  const dispatch = useAppDispatch();
  const hasDepartureDate = useAppSelector(
    (state) => state.product.hasDepartureDate
  );
  const hasRestoredContext = useAppSelector(
    (state) => state.product.hasRestoredContext
  );
  const [isChooseDateOpen, setIsChooseDateOpen] = useState<boolean>(false);
  const { isMobile } = useDevice();
  const { isLoading: isRudderstackLoading, travellerPageInitiated } =
    useRudderStack();
  const travellerStageHasInitiated = useRef(false);
  const validationHelper = new ValidationHelper(travellerValidationDispatch);

  const shouldSelectDate = useAppSelector(
    (state) => state.selectDate.shouldSelectDate
  );
  const { isCreatorHosted } = useCreatorHostedSession();
  // Creator-hosted sessions keep the creator's departure, so never ask for a
  // date while one is set. Without a date, fall back so the user is not stuck.
  const needsDateSelection =
    !hasDepartureDate || (shouldSelectDate && !isCreatorHosted);
  const [isEmailDisable, setIsEmailDisable] = useState(false);
  const [defaultPhoneCode, setDefaultPhoneCode] = useState<string | null>(null);
  const {
    isEnabled: fakeTravellerIsEnabled,
    isLoading: fakeTravellerIsLoading,
    fillWithFakeTraveller,
  } = useFakeTraveller({ dispatch: dispatchCreateTraveller });

  const {
    fillTraveller,
    isLoading: isFillTravellerLoading,
    hasTravellerData,
  } = useFillTraveller({
    dispatch: dispatchCreateTraveller,
    setDefaultPhoneCode,
    setIsEmailDisable,
  });
  const { submitTraveller } = useSubmitTraveller({
    state,
    validateTraveller,
    submit,
    selectedRooms,
  });
  const isTripQueryFetching = useIsFetching({ queryKey: ['trip'] });
  const isGotToGoLoading = useIsMutating({ mutationKey: ['got-to-go'] });
  const isGotToGoProcessing =
    !!isGotToGoLoading ||
    (createTravellerIsGotToGo && isCreateTravellerLoading);
  const { isDA } = useTenant();
  // DealsAway stays on Traveller until setup succeeds, so show progress.
  const isSubmitLoading =
    isDA && isCreateTravellerLoading && !createTravellerIsGotToGo;

  const bottomActionBarElement = document.getElementById('bottom-action-bar');
  const headerElement = document.getElementById('top-header');

  const viewportTop = headerElement
    ? headerElement.getBoundingClientRect().bottom
    : 0;
  const viewportBottom = bottomActionBarElement
    ? bottomActionBarElement.getBoundingClientRect().top
    : null;

  const scrollToInvalidField = () => {
    const inputErrors = [
      {
        id: 'first-name-input',
        isValid: validationHelper.checkFirstName(state.firstName),
      },
      {
        id: 'last-name-input',
        isValid: validationHelper.checkLastName(state.lastName),
      },
      {
        id: 'date-picker-container',
        isValid: validationHelper.checkDateOfBirth(state.date),
      },
      {
        id: 'phone-input',
        isValid: validationHelper.checkPhoneNumber(
          state.phoneNumber,
          state.phonePrefix?.value
        ),
      },
      {
        id: 'email-input',
        isValid: validationHelper.checkEmail(state.email),
      },
    ];

    const firstInvalidInput = inputErrors.find((i: any) => !i?.isValid);

    if (firstInvalidInput) {
      const inputElement = document.getElementById(firstInvalidInput.id);

      if (
        (inputElement &&
          inputElement.getBoundingClientRect().top < viewportTop) ||
        (inputElement &&
          viewportBottom &&
          inputElement.getBoundingClientRect().bottom > viewportBottom)
      ) {
        inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const clickHandler = () => {
    scrollToInvalidField();

    if (!needsDateSelection) {
      submitTraveller();
    } else {
      setIsChooseDateOpen(true);

      dispatch(setShouldSelectDate(false));
    }
  };

  const afterSubmitModal = (
    flexibleDeparture: boolean,
    selectedAvailability: SelectionAvailability,
    flexibleDepartureDecision: FlexibleDepartureDecision | null
  ) => {
    submitTraveller(
      flexibleDeparture,
      selectedAvailability,
      flexibleDepartureDecision
    );
  };

  const closeChooseDateModal = () => setIsChooseDateOpen(false);

  useEffect(() => {
    if (isRudderstackLoading || !hasRestoredContext) return;

    if (!travellerStageHasInitiated.current) {
      travellerPageInitiated();
      travellerStageHasInitiated.current = true;
    }
    fillTraveller();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRudderstackLoading, hasRestoredContext]);
  return (
    <>
      <div
        className="bg-light-600 md:border border-light-800 md:shadow-[0px_5px_5px_0px_#17181a0d] rounded-md"
      >
        <TravellerForm
          state={state}
          dispatch={dispatchCreateTraveller}
          validationState={travellerValidationState}
          validationDispatch={travellerValidationDispatch}
          isEmailDisable={isEmailDisable}
          defaultPhoneCode={defaultPhoneCode}
        />
        <div>
          <hr className="hidden md:block border-none h-px w-full bg-light-800" />
          <TravellerSubmit
            clickHandler={clickHandler}
            isDisabled={
              isFillTravellerLoading ||
              !!isTripQueryFetching ||
              isGotToGoProcessing ||
              isSubmitLoading
            }
            isLoading={isSubmitLoading}
            fillWithFakeTraveller={fillWithFakeTraveller}
            hasFakeTravellerButton={fakeTravellerIsEnabled && !hasTravellerData}
            fakeTravellerIsLoading={fakeTravellerIsLoading}
            submitButtonText={
              needsDateSelection ? 'Set Date' : 'Continue'
            }
          />
        </div>

        {isMobile && (
          <div className="fixed z-50 bottom-0 left-0 right-0 bg-light-600">
            <ContinueCta
              submitHandler={clickHandler}
              isDisabled={
                isFillTravellerLoading ||
                !!isTripQueryFetching ||
                isGotToGoProcessing ||
                isSubmitLoading
              }
              isLoading={isSubmitLoading}
              submitButtonText={
                needsDateSelection ? 'Set Date' : 'Continue'
              }
            />
          </div>
        )}
      </div>
      <ChangeDateModal
        isOpen={isChooseDateOpen}
        close={closeChooseDateModal}
        afterSubmit={afterSubmitModal}
      />
    </>
  );
};

export { TravellerStage };
